import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getEvents, ackEvent, getDevices } from '../api/endpoints';
import type { Event, EventType } from '../types';
import { formatDateTime } from '../utils/dates';
import { formatLatLon } from '../utils/coordinates';

const BRAND = '#00955F';

const EVENT_LABELS: Record<EventType, string> = {
  tracker: 'Rastreamento',
  help_me: 'SOS',
  low_battery: 'Bateria Fraca',
  move: 'Movimento',
  speed: 'Excesso de Velocidade',
  ac_alarm: 'Corte de Energia',
  stockade: 'Cerca Eletrônica',
  acc_on: 'Ignição Ligada',
  acc_off: 'Ignição Desligada',
  door_alarm: 'Porta Aberta',
  sensor_alarm: 'Sensor Ativado',
};

const EVENT_STYLES: Record<EventType, { bg: string; color: string }> = {
  tracker:      { bg: '#F3F2EF', color: '#7A7770' },
  help_me:      { bg: '#FADEDE', color: '#D32020' },
  low_battery:  { bg: '#FFE8CC', color: '#E07B00' },
  move:         { bg: '#FFE8CC', color: '#E07B00' },
  speed:        { bg: '#FFE8CC', color: '#E07B00' },
  ac_alarm:     { bg: '#FADEDE', color: '#D32020' },
  stockade:     { bg: '#EDE5FA', color: '#6D28D9' },
  acc_on:       { bg: '#E0F1E8', color: '#00955F' },
  acc_off:      { bg: '#F3F2EF', color: '#7A7770' },
  door_alarm:   { bg: '#FFE8CC', color: '#E07B00' },
  sensor_alarm: { bg: '#FFE8CC', color: '#E07B00' },
};

const selectStyle: React.CSSProperties = {
  height: 36, padding: '0 12px',
  border: '1px solid var(--color-border-strong)', borderRadius: 9,
  fontSize: 13, background: 'var(--color-bg-base)', color: 'var(--color-text)',
  outline: 'none', fontFamily: 'inherit',
};

const inputDateStyle: React.CSSProperties = {
  height: 36, padding: '0 12px',
  border: '1px solid var(--color-border-strong)', borderRadius: 9,
  fontSize: 13, background: 'var(--color-bg-base)', color: 'var(--color-text)',
  outline: 'none', fontFamily: 'inherit', colorScheme: 'light',
};

function osmUrl(lat: number, lon: number) {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}&zoom=16`;
}

export default function AlertsPage() {
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState('');
  const [filterDeviceId, setFilterDeviceId] = useState('');
  const [fromDate, setFromDate] = useState<string>(() => {
    const d = new Date(Date.now() - 7 * 24 * 3600 * 1000);
    return d.toISOString().slice(0, 16);
  });
  const [toDate, setToDate] = useState<string>(() => new Date().toISOString().slice(0, 16));

  const { data: devices = [] } = useQuery({ queryKey: ['devices'], queryFn: getDevices });

  const { data: eventsData, isFetching } = useQuery({
    queryKey: ['events', filterType, filterDeviceId, fromDate, toDate],
    queryFn: () => getEvents({
      type: filterType || undefined,
      deviceId: filterDeviceId ? Number(filterDeviceId) : undefined,
      from: fromDate ? new Date(fromDate).toISOString() : undefined,
      to: toDate ? new Date(toDate).toISOString() : undefined,
    }),
    refetchInterval: 30_000,
  });

  const events = eventsData?.data ?? [];

  const ackMutation = useMutation({
    mutationFn: ackEvent,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['events'] }); toast.success('Alarme reconhecido.'); },
    onError: () => toast.error('Falha ao reconhecer alarme.'),
  });

  const unackedCount = events.filter((e) => !e.ackedAt).length;

  return (
    <div className="flex flex-col h-full page-enter" style={{ background: 'var(--color-bg-base)' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid var(--color-border)', padding: '20px 24px 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div className="icon-badge">
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-text)', letterSpacing: '-0.02em', margin: 0, lineHeight: 1.2 }}>
              Alarmes
            </h1>
            {unackedCount > 0 ? (
              <p style={{ fontSize: 12, color: '#D32020', margin: '4px 0 0', fontWeight: 700 }}>
                {unackedCount} pendente{unackedCount !== 1 ? 's' : ''}
              </p>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--color-text-dim)', margin: '4px 0 0' }}>
                Nenhum alarme pendente
              </p>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <select value={filterDeviceId} onChange={(e) => setFilterDeviceId(e.target.value)} style={selectStyle}>
            <option value="">Todos os dispositivos</option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>{d.label || d.imei}</option>
            ))}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
            <option value="">Todos os tipos</option>
            {(Object.keys(EVENT_LABELS) as EventType[])
              .filter((t) => t !== 'tracker')
              .map((type) => (
                <option key={type} value={type}>{EVENT_LABELS[type]}</option>
              ))}
          </select>
          <input type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputDateStyle} />
          <input type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputDateStyle} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-thin" style={{ padding: 24 }}>
        {isFetching && events.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10, color: 'var(--color-text-dim)' }}>
            <svg style={{ animation: 'spin 1s linear infinite' }} width={18} height={18} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="var(--color-border-strong)" strokeWidth="4" />
              <path fill={BRAND} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Carregando...
          </div>
        ) : events.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12, color: 'var(--color-text-dim)' }}>
            <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span style={{ fontSize: 13 }}>Nenhum alarme encontrado.</span>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-base)' }}>
                  {['Tipo', 'Dispositivo', 'Hora', 'Localização', 'Ações'].map((h) => (
                    <th key={h} style={{
                      padding: '13px 20px', textAlign: 'left',
                      fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.14em', color: 'var(--color-text-dim)',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    onAck={() => ackMutation.mutate(event.id)}
                    isAcking={ackMutation.isPending && ackMutation.variables === event.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface EventRowProps {
  event: Event;
  onAck: () => void;
  isAcking: boolean;
}

function EventRow({ event, onAck, isAcking }: EventRowProps) {
  const label = EVENT_LABELS[event.eventType] ?? event.eventType;
  const style = EVENT_STYLES[event.eventType] ?? { bg: '#F3F2EF', color: '#7A7770' };

  return (
    <tr
      style={{ borderBottom: '1px solid var(--color-border)', background: !event.ackedAt ? 'rgba(255, 232, 204, 0.25)' : '' }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-bg-base)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = !event.ackedAt ? 'rgba(255, 232, 204, 0.25)' : ''; }}
    >
      <td style={{ padding: '13px 20px' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 10px', borderRadius: 9999,
          fontSize: 11.5, fontWeight: 700,
          background: style.bg, color: style.color,
        }}>
          {label}
        </span>
      </td>
      <td style={{ padding: '13px 20px', fontWeight: 700, color: 'var(--color-text)' }}>
        {event.device?.label || event.device?.imei || `#${event.deviceId}`}
      </td>
      <td style={{ padding: '13px 20px', fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
        {formatDateTime(event.occurredAt)}
      </td>
      <td style={{ padding: '13px 20px' }}>
        {event.position ? (
          <a
            href={osmUrl(event.position.lat, event.position.lon)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: BRAND, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
          >
            {formatLatLon(event.position.lat, event.position.lon)}
          </a>
        ) : (
          <span style={{ color: 'var(--color-border-strong)' }}>—</span>
        )}
      </td>
      <td style={{ padding: '13px 20px' }}>
        {event.ackedAt ? (
          <div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: BRAND }}>
              <svg width={14} height={14} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Reconhecido
            </span>
            {event.ackedBy && (
              <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 2 }}>por {event.ackedBy}</div>
            )}
          </div>
        ) : (
          <button
            onClick={onAck}
            disabled={isAcking}
            style={{
              padding: '6px 14px', background: BRAND, color: '#fff',
              fontSize: 12, fontWeight: 700, borderRadius: 8, border: 'none',
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: isAcking ? 0.6 : 1,
            }}
          >
            {isAcking && (
              <svg style={{ animation: 'spin 1s linear infinite' }} width={12} height={12} fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                <path fill="#fff" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            Reconhecer
          </button>
        )}
      </td>
    </tr>
  );
}
